import { Card, CardContent, Stack, Typography } from "@mui/material";
import { useState, useMemo } from "react";
import ProvenanceSankey from "../charts/ProvenanceSankey";
import FiltersProvenance from "../FiltersProvenance";
import sourceProvenanceSankdfJson from "@/_data/dashboard/source_provenance_sankdf_092826.json";
import type { ProvenanceRow, AnalyticsFilters } from "../types";

function normalizeFilterChoices(value: unknown): string[] {
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("{") && trimmed.includes('"choices"')) {
    try {
      const parsed = JSON.parse(trimmed) as { choices?: unknown };
      if (Array.isArray(parsed.choices)) {
        return parsed.choices
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // Fall through and return the original value.
    }
  }

  return [trimmed];
}

function matchesSelectedFilter(value: unknown, selected: string[]): boolean {
  if (selected.length === 0) return true;

  const choices = normalizeFilterChoices(value);
  if (choices.length === 0) return false;

  // Strict subset matching avoids keeping rows that carry unselected values.
  return choices.every((choice) => selected.includes(choice));
}

export default function ProvenanceTab() {
  const provenanceRows = sourceProvenanceSankdfJson.data as ProvenanceRow[];

  const documentAreaOptions = useMemo(() => {
    const set = new Set<string>();
    provenanceRows.forEach((row) => {
      if (row.continent && row.continent !== "") {
        set.add(row.continent);
      }
    });
    return Array.from(set).sort();
  }, [provenanceRows]);

  // Extract filter options from data
  const creditorRegionOptions = useMemo(() => {
    const set = new Set<string>();
    provenanceRows.forEach((row) => {
      normalizeFilterChoices(row.CreditorGeographicRegion).forEach((value) => {
        set.add(value);
      });
    });
    return Array.from(set).sort();
  }, [provenanceRows]);

  const borrowerRegionOptions = useMemo(() => {
    const set = new Set<string>();
    provenanceRows.forEach((row) => {
      normalizeFilterChoices(row.BorrowerGeographicRegion).forEach((value) => {
        set.add(value);
      });
    });
    return Array.from(set).sort();
  }, [provenanceRows]);

  const creditorTypeColumns = useMemo(() => {
    const set = new Set<string>();
    provenanceRows.forEach((row) => {
      normalizeFilterChoices(row.pCreditorType).forEach((value) => {
        set.add(value);
      });
    });
    return Array.from(set).sort();
  }, [provenanceRows]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    provenanceRows.forEach((row) => {
      normalizeFilterChoices(row.Source_General_Institution_Category).forEach(
        (value) => {
          set.add(value);
        },
      );
    });
    return Array.from(set).sort();
  }, [provenanceRows]);

  // Find min/max year
  const years = provenanceRows
    .map((row) => row.DocumentYear)
    .filter((y) => typeof y === "number" && !isNaN(y));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);

  // State for filters
  const [filters, setFilters] = useState<AnalyticsFilters>({
    selectedCreditorRegions: [],
    selectedBorrowerRegions: [],
    selectedCreditorTypes: [],
    selectedCategories: [],
    selectedDocumentAreas: [],
    selectedDenominations: [], // not used here
    yearRange: [minYear, maxYear],
    completeDataOnly: false,
  });

  // Filtering logic
  const filteredRows = useMemo(() => {
    return provenanceRows.filter((row) => {
      if (
        (filters.selectedDocumentAreas?.length ?? 0) > 0 &&
        !filters.selectedDocumentAreas?.includes(row.continent)
      ) {
        return false;
      }
      if (
        !matchesSelectedFilter(
          row.CreditorGeographicRegion,
          filters.selectedCreditorRegions,
        )
      ) {
        return false;
      }
      if (
        filters.selectedBorrowerRegions.length > 0 &&
        !normalizeFilterChoices(row.BorrowerGeographicRegion).some((value) =>
          filters.selectedBorrowerRegions.includes(value),
        )
      ) {
        return false;
      }
      if (
        filters.selectedCreditorTypes.length > 0 &&
        !normalizeFilterChoices(row.pCreditorType).some((value) =>
          filters.selectedCreditorTypes.includes(value),
        )
      ) {
        return false;
      }
      if (
        (filters.selectedCategories?.length ?? 0) > 0 &&
        !normalizeFilterChoices(row.Source_General_Institution_Category).some(
          (value) => filters.selectedCategories?.includes(value),
        )
      ) {
        return false;
      }
      if (
        row.DocumentYear < filters.yearRange[0] ||
        row.DocumentYear > filters.yearRange[1]
      ) {
        return false;
      }
      return true;
    });
  }, [provenanceRows, filters]);

  return (
    <Stack spacing={2.5}>
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            Sovereign debt must be accountable to the public. The visuals on
            this page show the original source of our collected agreements so
            that users can understand trends in sovereign debt transparency.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack spacing={5} sx={{ px: "1rem", py: "1.5rem" }}>
            <ProvenanceSankey rows={filteredRows} noCard />
            <FiltersProvenance
              filters={filters}
              setFilters={setFilters}
              documentAreaOptions={documentAreaOptions}
              creditorRegionOptions={creditorRegionOptions}
              borrowerRegionOptions={borrowerRegionOptions}
              creditorTypeColumns={creditorTypeColumns}
              categoryOptions={categoryOptions}
              minYear={minYear}
              maxYear={maxYear}
              horizontal
              noCard
            />
          </Stack>
          <Typography
            sx={{
              mt: 4,
              mb: 0,
              fontSize: "0.85rem",
              color: "#6b7280",
              textAlign: "right",
            }}
          >
            Last updated: 09/28/26
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}
